import { Campaign, CampaignClass, Event, Flight } from '@feathr/blackbox';

import type { TProductModel, TProductType } from './ConversionTableCard.types';

export interface IProductTypeConfig {
  type: TProductType;
  displayName: string;
  additionalColumns: string[];
  rerunContext: string;
  apiParamKey: string;
  getDetailsUrl: (model: TProductModel, campaignId: string, conversionId: string) => string;
}

export const PRODUCT_CONFIGS: Record<TProductType, IProductTypeConfig> = {
  event: {
    type: 'event',
    displayName: 'Project',
    additionalColumns: ['campaigns'],
    rerunContext: 'Project',
    apiParamKey: 'event',
    getDetailsUrl: (model, campaignId, conversionId) =>
      model.getItemUrl(`campaigns/${campaignId}/conversions/${conversionId}`),
  },
  flight: {
    type: 'flight',
    displayName: 'Flight',
    additionalColumns: ['campaigns'],
    rerunContext: 'Flight',
    apiParamKey: 'flight',
    getDetailsUrl: (model, campaignId, conversionId) =>
      model.getItemUrl(`campaigns/${campaignId}/conversions/${conversionId}`),
  },
  campaign: {
    type: 'campaign',
    displayName: 'Campaign',
    additionalColumns: [],
    rerunContext: 'Campaign',
    apiParamKey: 'campaign',
    getDetailsUrl: (model, _campaignId, conversionId) =>
      model.getItemUrl(`conversions/${conversionId}`),
  },
};

export function getProductType(model: TProductModel): TProductType {
  if (model instanceof Event) {
    return 'event';
  }
  if (model instanceof Flight) {
    return 'flight';
  }
  return 'campaign';
}

export function getProductConfig(model: TProductModel): IProductTypeConfig {
  return PRODUCT_CONFIGS[getProductType(model)];
}

export function isReferralCampaign(model: TProductModel): boolean {
  return model instanceof Campaign && model.get('_cls') === CampaignClass.Referral;
}

/**
 * Checks if the model is currently being rerun.
 * Only Campaign and Flight models support the rerun property.
 */
export function isModelRerunning(model: TProductModel): boolean {
  if (model instanceof Campaign) {
    return Boolean((model as Campaign).get('rerun'));
  }
  if (model instanceof Flight) {
    return Boolean((model as Flight).get('rerun'));
  }
  return false;
}

export const BASE_COLUMN_IDS = [
  'd_c',
  'person',
  'category',
  'goals',
  'value',
  'window',
  'timeline',
];

export function getDefaultColumnIds(
  model: TProductModel,
  hasTargetings: boolean,
  hasLocalUrl: boolean,
): string[] {
  const config = getProductConfig(model);
  const columnIds = [...BASE_COLUMN_IDS, ...config.additionalColumns];

  if (hasLocalUrl) {
    columnIds.push('details');
  }
  if (hasTargetings) {
    columnIds.push('source_target_segments');
  }
  if (isReferralCampaign(model)) {
    columnIds.push('partner', 'template');
  }

  return columnIds;
}
